<template>
  <base-message-box class="base-alert-box cq-container" :icon-name="icon" :type="type">
    <template slot="header">
      <div class="flex flex-col w-full">
        <div class="w-full flex gap-2 flex-row justify-between">
          <div class="flex">
            <span class="font-bold">{{ title }}</span>
            <span v-if="subtitle" class="cq-base-hidden cq-sm:inline cq-sm:w-auto mt-0 ml-4">{{ subtitle }}</span>
          </div>
          <span v-if="formattedDate" class="cq-base-hidden cq-md:inline cq-md:w-auto text-xs whitespace-nowrap">{{ formattedDate }}</span>
        </div>
        <span v-if="subtitle" class="cq-base-block w-full cq-sm:hidden mt-2">{{ subtitle }}</span>
      </div>
    </template>
    <template slot="content">
      <div v-safe-html="finalHtml"></div>
      <div v-if="formattedDate || hasActions" class="mt-3 flex flex-wrap justify-between">
        <div v-if="hasActions" class="flex flex-col items-start gap-2 md:flex-row md:flex-wrap font-semibold">
          <template v-for="(action, index) in actions">
            <router-link
              v-if="action.type === 'link'"
              class="hover:underline"
              :key="(action.IdAccion || index) + '-link'"
              :to="action.route"
              :target="action.target"
              :rel="action.target === '_blank' ? 'noopener noreferrer' : null"
            >{{ action.text }} →</router-link>
            <base-button v-else :key="(action.IdAccion || index) + '-button'" :mode="mode" :text="action.text" @click="handleClickActionEvent(action, $event)" />
          </template>
        </div>
        <span v-if="formattedDate" class="cq-base-block cq-md:hidden content-end ml-auto text-xs text-right whitespace-nowrap">{{ formattedDate }}</span>
      </div>     
    </template>
  </base-message-box>
</template>
<script>
import moment from 'moment'
import { i18n } from '@/i18n'
import MessageType from '@/classes/enums/MessageType'
import clearDateAtDay from '@/services/functions/dates/clearDateAt/days'
import toMoment from '@/services/functions/dates/toMoment'
import iconLibraryNames from '@/components/icons/_iconLibrary'
export default {
  name: 'base-alert-box',
  props: {
    icon: {
      type: String,
      default: '',
      validator: value => !value || iconLibraryNames.includes(value)
    },
    textContent: {
      type: [String, Array],
      default: ''
    },
    title: String,
    subtitle: String,
    mode: {
      type: String,
      default: 'secondary-phantom',
    },
    date: { type: [String, Number, Date] },
    type: {
      type: String,
      default: MessageType.INFO,
      validator: value => Object.values(MessageType).includes(value)
    },
    links: {
      type: Object,
      default: () => ({})
    },
    actions: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    formattedDate: function () {
      const source = toMoment(this.date)
      if (!source) return ''
      const now = moment()
      if (source.isAfter(now)) {
        return source.isSame(now, 'year') ? source.format('D MMM') : source.format('DD/MM/YYYY')
      }
      const seconds = now.diff(source, 'seconds')
      if (seconds < 15) return i18n.t('app.now')
      if (seconds < 60) return i18n.tc('unit_time_diff.seconds_ago', seconds)
      const minutes = now.diff(source, 'minutes')
      if (minutes < 60) return i18n.tc('unit_time_diff.minutes_ago', minutes)
      const hours = now.diff(source, 'hours')
      if (hours <= 2) return i18n.tc('unit_time_diff.hours_ago', hours)
      const sourceAtDays = clearDateAtDay(source, true)
      const nowAtDays = clearDateAtDay(now, true)
      if (sourceAtDays.isSame(nowAtDays, 'day')) return source.format('HH:mm[h]')
      const days = nowAtDays.diff(sourceAtDays, 'days')
      if (days === 1) return i18n.t('unit_time_diff.yesterday_at', { time: source.format('HH:mm') })
      if (days < 7) return i18n.tc('unit_time_diff.days_ago', days)
      if (sourceAtDays.isSame(nowAtDays, 'year')) return source.format('D MMM')
      return source.format('DD/MM/YYYY')
    },
    finalHtml: function () {
      const hasLinks = Object.keys(this.links || {}).length
      const process = (str) => {
        if (!hasLinks) return str
        return str.replace(/#LINK:(\w+)#/g, (full, key) => {
          const value = this.links[key]
          if (!value?.text && !value?.url) return full
          const text = value.text || value.url
          const url = value.url
          if (!url) return text
          return `<a class="${this.darkMode ? 'text-primary' : 'text-primary-800'}" href="${url}" target="_blank">${text}</a>`
        })
      }
      if (typeof this.textContent === 'string') {
        return `<p>${process(this.textContent)}</p>`
      }
      if (Array.isArray(this.textContent)) {
        return `<p>${this.textContent.map(String).map(process).join('</p><p>')}</p>`
      }
      return `<p>${process(String(this.textContent))}</p>`
    },
    hasActions: function () {
      return !!this.actions?.length
    }
  },
  methods: {
    handleClickActionEvent (action, event) {
      if (typeof action.handler === 'function') {
        action.handler({ action, event })
      } else {
        this.$emit('action-click', { action, event })
      }
    }
  }
}
</script>
<style scoped>
.el-message {
  --alert-left-border-width: 10px;
  --alert-left-border-overlay: rgba(0, 0, 0, 0.08);
  position: relative;
  display: block;
  transform: none;
  left: auto;
  top: auto;
  transition: none;
  z-index: inherit !important;
  border-left-width: var(--alert-left-border-width);
  overflow: visible;
}

.el-message::before {
  content: '';
  position: absolute;
  top: -1px;
  bottom: -1px;
  left: calc(-1 * var(--alert-left-border-width));
  width: var(--alert-left-border-width);
  background-color: var(--alert-left-border-overlay);
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  pointer-events: none;
}
</style>
<style>
:where(.cq-base-hidden) {
  display: none;
}

:where(.cq-base-block) {
  display: block;
}
</style>
